
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { INITIAL_CONFIG } from './constants';
import { ProductConfig, SelectedConfig, ViewMode, ComponentOption, CompanyInfo } from './types';
import Visualizer from './components/Visualizer';
import AdminPanel from './components/AdminPanel';
import OrderSummary from './components/OrderSummary';
import { supabase } from './supabase';
import { toPng } from 'html-to-image';

const DEFAULT_COMPANY_INFO: CompanyInfo = {
  logoUrl: '',
  companyName: 'The Bedmaker B.V.',
  authorizedPerson: 'Showroom Manager',
  address: 'Amsterdam, Nederland',
  phone: '+31 20 123 4567',
  email: 'info@thebedmaker.com',
  website: 'www.thebedmaker.com'
};

const SectionTitle = ({ children }: { children?: React.ReactNode }) => (
  <h3 className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4 border-b border-gray-50 pb-1.5">{children}</h3>
);

interface OptionCardProps {
  item: ComponentOption;
  isSelected: boolean;
  onClick: () => void;
  view?: 'diagonal' | 'front';
}

const OptionBlock: React.FC<OptionCardProps> = ({ item, isSelected, onClick, view = 'diagonal' }) => {
  const displayImage = view === 'front' ? (item.imageUrlFront || item.imageUrl) : (item.imageUrl || item.imageUrlFront);

  return (
    <button 
      onClick={onClick}
      className={`flex flex-col items-center p-1 rounded-xl border transition-all duration-300 group relative aspect-square ${isSelected ? 'border-[#0a0c14] bg-white ring-2 ring-[#0a0c14]/5 shadow-md scale-105 z-10' : 'border-gray-100 bg-white hover:border-gray-300'}`}
    >
      <div className="w-full flex-1 bg-gray-50/20 rounded-lg overflow-hidden flex items-center justify-center relative">
        {displayImage ? (
          <img src={displayImage} alt={item.name} className="w-full h-full object-contain opacity-100 transition-transform duration-500 group-hover:scale-110" />
        ) : (
          <div className="text-[7px] text-gray-300 font-bold uppercase text-center leading-none">Geen Foto</div>
        )}
      </div>
      <div className="text-center w-full pt-1.5 pb-0.5 px-0.5">
        <p className={`font-bold text-[8.5px] leading-tight truncate w-full ${isSelected ? 'text-[#0a0c14]' : 'text-gray-900'}`}>{item.name}</p>
        <p className="text-[7.5px] text-gray-400 font-medium mt-0.5">{item.priceModifier === 0 ? 'Incl.' : `+€${item.priceModifier}`}</p>
      </div>
    </button>
  );
};

const StepIcon = ({ type, active }: { type: string, active: boolean }) => {
  const colorClass = active ? "text-black" : "text-gray-400";
  if (type === 'Hoofdbord') return <svg className={`w-6 h-6 ${colorClass}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 18V7c0-1.1.9-2 2-2h16a2 2 0 0 1 2 2v11" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 14h20M7 5v4M17 5v4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
  if (type === 'Box') return <svg className={`w-6 h-6 ${colorClass}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="10" width="18" height="9" rx="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 14h18M7 10v4M17 10v4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
  if (type === 'Matras') return <svg className={`w-6 h-6 ${colorClass}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 8h16M4 12h16M4 16h16" strokeLinecap="round" strokeLinejoin="round"/><rect x="2" y="6" width="20" height="12" rx="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
  if (type === 'Poot') return <svg className={`w-6 h-6 ${colorClass}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 18l-1 3M18 18l1 3M6 10v8M18 10v8M4 10h16" strokeLinecap="round" strokeLinejoin="round"/></svg>;
  return <svg className={`w-6 h-6 ${colorClass}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 8v8M8 12h8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
};

const App: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.CUSTOMER);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSummaryVisible, setIsSummaryVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const visualizerRef = useRef<HTMLDivElement>(null);
  
  // Accordion states for Extras step
  const [isNightstandOpen, setIsNightstandOpen] = useState(false);
  const [isPuffOpen, setIsPuffOpen] = useState(false);

  const [configs, setConfigs] = useState<ProductConfig[]>([INITIAL_CONFIG]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(DEFAULT_COMPANY_INFO);
  const [activeConfigId, setActiveConfigId] = useState<string>(INITIAL_CONFIG.id);

  useEffect(() => {
    async function loadCloudData() {
      try {
        const { data, error } = await supabase.from('settings').select('configs, company_info').eq('id', 'main').single();
        if (data) {
          if (data.configs?.length) { setConfigs(data.configs); setActiveConfigId(data.configs[0].id); }
          if (data.company_info) setCompanyInfo(data.company_info);
        }
      } catch (e) { console.error(e); } finally { setIsLoading(false); }
    }
    loadCloudData();
  }, []);

  const activeConfig = useMemo(() => configs.find(c => c.id === activeConfigId) || configs[0] || INITIAL_CONFIG, [configs, activeConfigId]);

  const [selection, setSelection] = useState<SelectedConfig>({
    sizeId: '', colorId: '', mattressId: '', baseId: '', headboardId: '',
    headboardVariantId: '', nightstandId: 'n0', hasTopper: false, topperId: '',
    hasVoetbord: false, voetbordId: '', hasVoet: false, voetId: '', pootId: ''
  });

  useEffect(() => {
    if (isLoading) return;
    const firstH = activeConfig.headboards[0];
    const rVariant = firstH?.variants?.find(v => v.label === 'R');
    setSelection({
      sizeId: activeConfig.sizes[0]?.id || '',
      colorId: activeConfig.colors[0]?.id || '',
      mattressId: activeConfig.mattresses[0]?.id || '',
      baseId: activeConfig.bases[0]?.id || '',
      headboardId: firstH?.id || '',
      headboardVariantId: rVariant?.id || firstH?.variants?.[0]?.id || '',
      nightstandId: 'n0',
      hasTopper: false,
      topperId: activeConfig.toppers[0]?.id || '',
      hasVoetbord: false,
      voetbordId: activeConfig.voetbords[0]?.id || '',
      hasVoet: false,
      voetId: activeConfig.voets[0]?.id || '',
      pootId: activeConfig.poets[0]?.id || ''
    });
  }, [activeConfigId, activeConfig, isLoading]);

  const totalPrice = useMemo(() => {
    const s = activeConfig.sizes.find(x => x.id === selection.sizeId)?.priceModifier || 0;
    const c = activeConfig.colors.find(x => x.id === selection.colorId)?.priceModifier || 0;
    const m = activeConfig.mattresses.find(x => x.id === selection.mattressId)?.priceModifier || 0;
    const b = activeConfig.bases.find(x => x.id === selection.baseId)?.priceModifier || 0;
    const hObj = activeConfig.headboards.find(x => x.id === selection.headboardId);
    const h = hObj?.priceModifier || 0;
    const hv = hObj?.variants?.find(v => v.id === selection.headboardVariantId)?.priceModifier || 0;
    const n = activeConfig.nightstands.find(x => x.id === selection.nightstandId)?.priceModifier || 0;
    const t = selection.hasTopper ? (activeConfig.toppers.find(x => x.id === selection.topperId)?.priceModifier || 0) : 0;
    const vb = selection.hasVoetbord ? (activeConfig.voetbords.find(x => x.id === selection.voetbordId)?.priceModifier || 0) : 0;
    const v = selection.hasVoet ? (activeConfig.voets.find(x => x.id === selection.voetId)?.priceModifier || 0) : 0;
    const p = activeConfig.poets.find(x => x.id === selection.pootId)?.priceModifier || 0;
    return activeConfig.basePrice + s + c + m + b + h + hv + n + t + vb + v + p;
  }, [activeConfig, selection]);

  const handleShowSummary = async () => {
    if (visualizerRef.current) {
      try {
        const dataUrl = await toPng(visualizerRef.current, {
          cacheBust: true,
          backgroundColor: '#ffffff'
        });
        setPreviewImage(dataUrl);
      } catch (err) {
        console.error('Failed to capture preview', err);
      }
    }
    setIsSummaryVisible(true);
  };

  const steps = [{ label: 'Basis' }, { label: 'Hoofdbord' }, { label: 'Box' }, { label: 'Matras' }, { label: 'Poot' }, { label: 'Extras' }];

  if (isLoading) return <div className="min-h-screen flex items-center justify-center font-black uppercase tracking-widest bg-white">Laden...</div>;
  if (viewMode === ViewMode.ADMIN) return <AdminPanel configs={configs} activeId={activeConfigId} companyInfo={companyInfo} onUpdateCompanyInfo={setCompanyInfo} onUpdateConfigs={setConfigs} onSetActiveId={setActiveConfigId} onClose={() => setViewMode(ViewMode.CUSTOMER)} />;
  if (isSummaryVisible) return <OrderSummary config={activeConfig} companyInfo={companyInfo} selection={selection} totalPrice={totalPrice} previewImage={previewImage} onClose={() => setIsSummaryVisible(false)} />;

  return (
    <div className="min-h-screen bg-[#FBFBFD] flex flex-col font-sans text-[#1d1d1f]">
      {isAuthModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <div className="bg-white w-full max-w-[320px] rounded-[2rem] p-8 shadow-2xl relative animate-in zoom-in duration-300">
            <button onClick={() => setIsAuthModalOpen(false)} className="absolute top-6 right-6 text-gray-300 hover:text-black transition-colors">✕</button>
            <h3 className="text-sm font-black uppercase tracking-widest text-center mb-6">Admin Login</h3>
            <form onSubmit={(e) => { e.preventDefault(); if (passwordInput === 'Evo123') { setViewMode(ViewMode.ADMIN); setIsAuthModalOpen(false); } }} className="space-y-4">
              <input type="password" value={passwordInput} onChange={(e) => setPasswordInput(e.target.value)} placeholder="••••••••" className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl outline-none text-center font-bold tracking-widest focus:ring-2 ring-black/5" />
              <button type="submit" className="w-full bg-black text-white py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-zinc-800 transition-all">Login</button>
            </form>
          </div>
        </div>
      )}
      
      <nav className="bg-white border-b border-gray-100 px-6 lg:px-12 py-3 flex justify-between items-center sticky top-0 z-[100] shadow-sm">
        <div className="flex flex-col"><h1 className="text-base font-bold tracking-tight">The Bedmaker</h1><span className="text-[7px] font-black text-gray-400 uppercase tracking-[0.3em]">Pro v5.0</span></div>
        <div className="flex items-center gap-6 lg:gap-12">
          <button onClick={() => setIsAuthModalOpen(true)} className="text-[9px] font-bold text-gray-300 hover:text-black uppercase tracking-widest transition-colors">Admin</button>
          <div className="text-right flex flex-col"><span className="text-[7px] font-black text-gray-300 uppercase mb-0.5">TOTAALPRIJS</span><span className="text-xl font-bold">€{totalPrice.toLocaleString()}</span></div>
        </div>
      </nav>

      <main className="flex-1 flex flex-col lg:flex-row lg:h-[calc(100vh-64px)] lg:overflow-hidden">
        <div className="lg:w-3/5 py-4 px-4 lg:p-10 flex flex-col justify-center items-center bg-white lg:h-full relative">
          <div ref={visualizerRef} className="w-full max-w-[850px]"><Visualizer config={activeConfig} selection={selection} /></div>
        </div>
        <div className="lg:w-2/5 flex flex-col bg-[#FBFBFD] border-t lg:border-t-0 lg:border-l border-gray-100 shadow-inner h-auto lg:h-full">
          <div className="bg-white p-6 border-b border-gray-100 shadow-sm z-20">
             <div className="flex items-center justify-between gap-1 bg-[#F2F2F7] p-1.5 rounded-[2.25rem]">
                {steps.map((s, idx) => (
                  <button key={idx} onClick={() => setCurrentStep(idx)} className={`flex-1 flex flex-col items-center py-4 rounded-[1.75rem] transition-all duration-500 ease-out group ${currentStep === idx ? 'bg-white shadow-[0_8px_20px_rgb(0,0,0,0.06)] scale-100 text-black border border-gray-100/50' : 'text-gray-400 hover:text-gray-600 hover:bg-white/40'}`}>
                    <StepIcon type={s.label} active={currentStep === idx} /><span className="text-[7.5px] font-black uppercase mt-1 px-1">{s.label.split(' ')[0]}</span>
                  </button>
                ))}
             </div>
          </div>
          <div className="flex-1 lg:overflow-y-auto px-8 py-6">
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              {currentStep === 0 && (
                <div className="space-y-12">
                   <section><SectionTitle>Afmeting</SectionTitle><div className="grid grid-cols-2 gap-3">{activeConfig.sizes.map(s => <button key={s.id} onClick={() => setSelection({...selection, sizeId: s.id})} className={`flex flex-col items-center py-5 px-4 rounded-2xl border transition-all ${selection.sizeId === s.id ? 'border-black bg-white ring-1 ring-black shadow-md scale-[1.02]' : 'border-gray-100 bg-white hover:border-gray-200'}`}><div className="text-sm font-black">{s.label} cm</div>{s.priceModifier > 0 && <div className="text-[9px] font-bold text-gray-400 mt-1">+ €{s.priceModifier}</div>}</button>)}</div></section>
                   <section><SectionTitle>Kleur & Stof</SectionTitle><div className="grid grid-cols-5 gap-4">{activeConfig.colors.map(c => <button key={c.id} onClick={() => setSelection({...selection, colorId: c.id})} className={`relative w-11 h-11 rounded-full border-2 transition-all ${selection.colorId === c.id ? 'border-black ring-2 ring-offset-2 ring-black scale-110' : 'border-transparent opacity-60 hover:opacity-100 hover:scale-110'}`} style={{ backgroundColor: c.hex }}>{c.imageUrl && <img src={c.imageUrl} className="w-full h-full rounded-full object-cover" />}</button>)}</div></section>
                </div>
              )}
              {currentStep === 1 && (
                <div className="space-y-10">
                  <section><SectionTitle>Hoofdbord Model</SectionTitle><div className="grid grid-cols-3 gap-3">{activeConfig.headboards.map(h => <OptionBlock key={h.id} item={h} isSelected={selection.headboardId === h.id} onClick={() => { const r = h.variants?.find(v => v.label === 'R'); setSelection({...selection, headboardId: h.id, headboardVariantId: r?.id || h.variants?.[0]?.id || ''}); }} view="front" />)}</div></section>
                  <section><SectionTitle>Formaat (R - M - XL - XXL)</SectionTitle><div className="flex flex-wrap gap-2">{activeConfig.headboards.find(h => h.id === selection.headboardId)?.variants?.map(v => <button key={v.id} onClick={() => setSelection({...selection, headboardVariantId: v.id})} className={`flex-1 py-3 px-4 rounded-xl border transition-all ${selection.headboardVariantId === v.id ? 'border-black bg-white ring-1 ring-black shadow-md' : 'border-gray-100 bg-white hover:border-gray-200'}`}><div className="text-[10px] font-black uppercase">{v.label}</div>{v.priceModifier > 0 && <div className="text-[8px] text-gray-400 mt-1 font-bold">+€{v.priceModifier}</div>}</button>)}</div></section>
                </div>
              )}
              {currentStep === 2 && <section><SectionTitle>Box / Bodem</SectionTitle><div className="grid grid-cols-3 gap-3">{activeConfig.bases.map(b => <OptionBlock key={b.id} item={b} isSelected={selection.baseId === b.id} onClick={() => setSelection({...selection, baseId: b.id})} />)}</div></section>}
              {currentStep === 3 && (
                <div className="space-y-10">
                  <section>
                    <SectionTitle>Matras</SectionTitle>
                    <div className="grid grid-cols-3 gap-3">
                      {activeConfig.mattresses.map(m => (
                        <OptionBlock 
                          key={m.id} 
                          item={m} 
                          isSelected={selection.mattressId === m.id} 
                          onClick={() => setSelection({...selection, mattressId: m.id})} 
                        />
                      ))}
                    </div>
                  </section>
                  <section>
                    <SectionTitle>Toppers</SectionTitle>
                    <div className="grid grid-cols-3 gap-3">
                      <button 
                        onClick={() => setSelection({...selection, hasTopper: false, topperId: ''})} 
                        className={`flex flex-col items-center justify-center p-2 rounded-xl border aspect-square transition-all ${!selection.hasTopper ? 'border-black bg-white ring-1 ring-black shadow-md' : 'border-gray-100 bg-white'}`}
                      >
                        <div className="text-[10px] font-black uppercase">Geen</div>
                      </button>
                      {activeConfig.toppers.map(t => (
                        <OptionBlock 
                          key={t.id} 
                          item={t} 
                          isSelected={selection.hasTopper && selection.topperId === t.id} 
                          onClick={() => setSelection({...selection, hasTopper: true, topperId: t.id})} 
                        />
                      ))}
                    </div>
                  </section>
                </div>
              )}
              {currentStep === 4 && <section><SectionTitle>Bedpootjes</SectionTitle><div className="grid grid-cols-4 gap-3">{activeConfig.poets.map(p => <OptionBlock key={p.id} item={p} isSelected={selection.pootId === p.id} onClick={() => setSelection({...selection, pootId: p.id})} view="front" />)}</div></section>}
              {currentStep === 5 && (
                <div className="space-y-12 pb-10">
                  <section>
                    <SectionTitle>Voetborden</SectionTitle>
                    <div className="grid grid-cols-3 gap-3">
                      <button 
                        onClick={() => setSelection({...selection, hasVoetbord: false, voetbordId: ''})} 
                        className={`flex flex-col items-center justify-center p-2 rounded-xl border aspect-square transition-all ${!selection.hasVoetbord ? 'border-black bg-white ring-1 ring-black shadow-md' : 'border-gray-100 bg-white'}`}
                      >
                        <div className="text-[10px] font-black uppercase">Geen</div>
                      </button>
                      {activeConfig.voetbords.map(v => (
                        <OptionBlock 
                          key={v.id} 
                          item={v} 
                          isSelected={selection.hasVoetbord && selection.voetbordId === v.id} 
                          onClick={() => setSelection({...selection, hasVoetbord: true, voetbordId: v.id})} 
                        />
                      ))}
                    </div>
                  </section>

                  {/* Toggleable NACHTKASTJES */}
                  <section className="border border-gray-100 rounded-[2rem] bg-white overflow-hidden shadow-sm">
                    <button 
                      onClick={() => setIsNightstandOpen(!isNightstandOpen)}
                      className="w-full flex items-center justify-between px-8 py-5 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex flex-col items-start">
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Extra Optie</span>
                        <h4 className="text-sm font-bold text-gray-900">Nachtkastjes</h4>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-[10px] font-black text-gray-300 uppercase">{isNightstandOpen ? 'Sluiten' : 'Openen'}</span>
                        <div className={`w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center transition-transform duration-300 ${isNightstandOpen ? 'rotate-180' : ''}`}>
                          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                      </div>
                    </button>
                    {isNightstandOpen && (
                      <div className="px-8 pb-8 animate-in fade-in slide-in-from-top-4 duration-300">
                        <div className="grid grid-cols-3 gap-3 pt-4">
                          <button 
                            onClick={() => setSelection({...selection, nightstandId: 'n0'})} 
                            className={`flex flex-col items-center justify-center p-2 rounded-xl border aspect-square transition-all ${selection.nightstandId === 'n0' ? 'border-black bg-white ring-1 ring-black shadow-md' : 'border-gray-100 bg-white'}`}
                          >
                            <div className="text-[10px] font-black uppercase">Geen</div>
                          </button>
                          {activeConfig.nightstands.map(n => (
                            <OptionBlock 
                              key={n.id} 
                              item={n} 
                              isSelected={selection.nightstandId === n.id} 
                              onClick={() => setSelection({...selection, nightstandId: n.id})} 
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </section>

                  {/* Toggleable PUFFS & BANKJES */}
                  <section className="border border-gray-100 rounded-[2rem] bg-white overflow-hidden shadow-sm">
                    <button 
                      onClick={() => setIsPuffOpen(!isPuffOpen)}
                      className="w-full flex items-center justify-between px-8 py-5 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex flex-col items-start">
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Extra Optie</span>
                        <h4 className="text-sm font-bold text-gray-900">Puffs & Bankjes</h4>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-[10px] font-black text-gray-300 uppercase">{isPuffOpen ? 'Sluiten' : 'Openen'}</span>
                        <div className={`w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center transition-transform duration-300 ${isPuffOpen ? 'rotate-180' : ''}`}>
                          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                      </div>
                    </button>
                    {isPuffOpen && (
                      <div className="px-8 pb-8 animate-in fade-in slide-in-from-top-4 duration-300">
                        <div className="grid grid-cols-3 gap-3 pt-4">
                          <button 
                            onClick={() => setSelection({...selection, hasVoet: false, voetId: ''})} 
                            className={`flex flex-col items-center justify-center p-2 rounded-xl border aspect-square transition-all ${!selection.hasVoet ? 'border-black bg-white ring-1 ring-black shadow-md' : 'border-gray-100 bg-white'}`}
                          >
                            <div className="text-[10px] font-black uppercase">Geen</div>
                          </button>
                          {activeConfig.voets.map(vt => (
                            <OptionBlock 
                              key={vt.id} 
                              item={vt} 
                              isSelected={selection.hasVoet && selection.voetId === vt.id} 
                              onClick={() => setSelection({...selection, hasVoet: true, voetId: vt.id})} 
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </section>
                </div>
              )}
            </div>
          </div>
          <div className="p-6 border-t border-gray-100 flex gap-3 bg-white sticky bottom-0 z-30">
            <button onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))} className="px-6 py-4 border border-gray-100 text-gray-300 font-black rounded-xl text-[9px] uppercase tracking-widest">Vorige</button>
            <button onClick={() => currentStep < steps.length - 1 ? setCurrentStep(prev => prev + 1) : handleShowSummary()} className="flex-1 bg-black text-white font-black py-4 rounded-xl shadow-xl hover:bg-zinc-800 transition-all text-[9px] uppercase tracking-widest">
              {currentStep < steps.length - 1 ? 'Volgende' : 'Overzicht'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default App;
